<script setup lang="ts">
  import type { Attachment, Model } from '@api/models';
  import { ref, watch } from 'vue';

  import { Button } from '@ui/components/button';
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
  } from '@ui/components/dialog';

  import { AttachmentPresignItem } from '@/components/attachments';
  import {
    FileDropzoneContent,
    FileDropzoneOverlay,
    FileDropzoneProvider,
    LayoutDropzoneProgressItem,
  } from '@/components/file-upload-dropzone';

  interface Props {
    entityId?: string;
    entityModel?: Model & { id?: string };
    entityName?: string;
    fileTypes?: string[];
    multiple?: boolean;
    presignUrlFnName?: string;
    presignUrlParams?: Record<string, any>;
    title?: string;
  }

  withDefaults(defineProps<Props>(), {
    multiple: true,
  });

  const emit = defineEmits<{
    (e: 'newFileAttachments', fileAttachments: Attachment[]): void;
    (e: 'uploaded', entity: any): void;
  }>();

  const isOpen = defineModel<boolean>('isOpen', { default: false });
  const DropzoneProviderRef = ref<InstanceType<typeof FileDropzoneProvider> | null>(null);
  const DialogRef = ref<InstanceType<typeof Dialog> | null>(null);

  defineExpose({
    DialogRef,
    DropzoneProviderRef,
    hide: () => {
      isOpen.value = false;
    },
    show: () => {
      isOpen.value = true;
    },
  });

  watch(isOpen, (value) => {
    if (!value) {
      DropzoneProviderRef.value?.clearFileAttachments?.();
    }
  });

  const onNewFileAttachments = (fileAttachments: Attachment[]) => {
    emit('newFileAttachments', fileAttachments);
  };
</script>

<template>
  <FileDropzoneProvider
    ref="DropzoneProviderRef"
    v-slot="{ openFileInput, fileAttachments, isOverDropZone, isSingleUploadLimitReached }"
    :file-types="fileTypes"
    :multiple="multiple"
    @on-new-file-attachments="onNewFileAttachments"
  >
    <Dialog ref="DialogRef" :open="isOpen" :modal="true" @update:open="(value) => (isOpen = value)">
      <DialogTrigger v-if="$slots.upload_file_button" as-child>
        <slot name="upload_file_button" />
      </DialogTrigger>

      <DialogContent class="w-[650px] min-w-[650px] p-0 gap-0">
        <DialogHeader class="flex flex-col items-start border-b px-6 py-3 text-left gap-3">
          <DialogTitle class="space-x-3.5 text-base">
            <i class="fa-regular fa-upload"></i>
            <span>{{ title || 'Upload Files' }}</span>
          </DialogTitle>

          <slot name="header-extension" v-bind="{ fileAttachments }" />
        </DialogHeader>

        <FileDropzoneContent class="cursor-pointer relative">
          <div class="space-y-6 p-2.5">
            <div class="bg-secondary p-3.5 h-72 rounded flex items-center justify-center">
              <div
                class="flex flex-col items-center justify-center rounded bg-background w-full border border-dashed border-border h-full text-center space-y-3.5"
                :class="isSingleUploadLimitReached ? '' : 'cursor-pointer'"
                @click="
                  () => {
                    if (isSingleUploadLimitReached) return;

                    openFileInput();
                  }
                "
              >
                <i class="fa-light fa-cloud-arrow-up text-[40px] text-muted-foreground"></i>

                <p class="text-xs">
                  Drag and Drop files here or
                  <Button :disabled="isSingleUploadLimitReached" variant="link" size="inline">
                    Choose file(s)
                  </Button>
                </p>
              </div>
            </div>
          </div>

          <slot
            v-bind="{
              openFileInput,
              fileAttachments,
              isOverDropZone,
              isSingleUploadLimitReached,
            }"
          />

          <div v-if="fileAttachments?.length" class="px-2.5 space-y-2.5 mb-2.5">
            <AttachmentPresignItem
              v-for="(attachment, index) in fileAttachments"
              :key="index"
              v-slot="{ progressValue, filesize }"
              :attachment="attachment"
              :entity-name="entityName"
              :entity-id="entityId"
              :presign-url-fn-name="presignUrlFnName"
              :presign-url-params="presignUrlParams"
              :entity-model="entityModel"
              @uploaded="(entity) => emit('uploaded', entity)"
            >
              <LayoutDropzoneProgressItem
                :attachment="attachment"
                :progress-value="progressValue"
                :filesize="filesize"
              />
            </AttachmentPresignItem>
          </div>

          <FileDropzoneOverlay />
        </FileDropzoneContent>

        <DialogFooter class="px-2.5 pb-2.5">
          <slot
            name="dialog_actions"
            v-bind="{
              openFileInput,
              fileAttachments,
              isOverDropZone,
              isSingleUploadLimitReached,
            }"
          />

          <Button v-if="!isSingleUploadLimitReached" size="sm" @click="() => openFileInput()">
            <template v-if="fileAttachments?.length"> Upload more </template>

            <template v-else> Upload </template>
          </Button>

          <Button variant="secondary" size="sm" @click="isOpen = false">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </FileDropzoneProvider>
</template>

<style scoped></style>
